import { createAsyncThunk } from '@reduxjs/toolkit';

import type { MovimentarPendenciasResultado } from '@/types/processoArbitral/pendenciasMovimentacao';

import type { InternoAndamento, InternoPendenciaFiltro, InternoPendenciasResult } from '../../types';
import { processoInternoService } from './services';

const reject = (error: unknown): string =>
  error instanceof Error ? error.message : 'Erro desconhecido';

export const fetchInternoAndamentos = createAsyncThunk<
  { processoId: string; items: InternoAndamento[] },
  string,
  { rejectValue: string }
>('chatAtendimento/processoInterno/andamentos', async (processoId, { rejectWithValue }) => {
  try {
    const items = await processoInternoService.listAndamentos(processoId);
    return { processoId, items };
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const fetchInternoPendencias = createAsyncThunk<
  { processoId: string; filtro: InternoPendenciaFiltro; data: InternoPendenciasResult },
  { processoId: string; filtro: InternoPendenciaFiltro },
  { rejectValue: string }
>('chatAtendimento/processoInterno/pendencias', async ({ processoId, filtro }, { rejectWithValue }) => {
  try {
    const apiFiltro = filtro === 'concluidas' ? 'finalizadas' : filtro === 'urgentes' ? 'ativas' : 'todas';
    const data = await processoInternoService.listPendencias(processoId, apiFiltro);
    return { processoId, filtro, data };
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const tornarMensagemAndamento = createAsyncThunk<
  InternoAndamento,
  { conversaId: string; mensagemId: string },
  { rejectValue: string }
>(
  'chatAtendimento/processoInterno/tornarAndamento',
  async ({ conversaId, mensagemId }, { rejectWithValue }) => {
    try {
      return await processoInternoService.tornarAndamento(conversaId, mensagemId);
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);

export const removerSeloAndamento = createAsyncThunk<
  { mensagemId: string },
  { conversaId: string; mensagemId: string },
  { rejectValue: string }
>(
  'chatAtendimento/processoInterno/removerSelo',
  async ({ conversaId, mensagemId }, { rejectWithValue }) => {
    try {
      await processoInternoService.removerSelo(conversaId, mensagemId);
      return { mensagemId };
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);

export const excluirInternoAndamento = createAsyncThunk<
  { andamentoId: string },
  { processoId: string; andamentoId: string; motivo?: string },
  { rejectValue: string }
>(
  'chatAtendimento/processoInterno/excluirAndamento',
  async ({ processoId, andamentoId, motivo }, { rejectWithValue }) => {
    try {
      await processoInternoService.excluirAndamento(processoId, andamentoId, motivo);
      return { andamentoId };
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);

export const responderInternoPendencias = createAsyncThunk<
  MovimentarPendenciasResultado,
  { processoArbitralId: string; pendenciaIds: string[]; resposta: string },
  { rejectValue: string }
>(
  'chatAtendimento/processoInterno/responder',
  async ({ processoArbitralId, pendenciaIds, resposta }, { rejectWithValue }) => {
    try {
      return await processoInternoService.responderPendencias({
        clientRequestId: crypto.randomUUID(),
        processoArbitralId,
        pendenciaIds,
        desfecho: 'RESOLVER',
        resposta,
        anexoUploadIds: [],
      });
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);
